import { MdOutlineClear } from 'react-icons/md';
import { Button, Tab, Tabs } from '@ds';
import { FilterDropdown } from '@ui';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useInfiniteHospitalListQuery } from '@/queries/hospital';

type HospitalContactFiltersProps<T extends Record<string, unknown>> = {
  filters: Record<keyof T, string | string[]>;
  hasFilters: boolean;
  updateFilters: (filter: string | string[], type: keyof T) => void;
  clearFilters: () => void;
};

export const HospitalContactFilters = <T extends Record<string, unknown>>({
  filters,
  hasFilters,
  updateFilters,
  clearFilters,
}: HospitalContactFiltersProps<T>) => {
  const {
    list: locationList,
    isLoading,
    hasNextPage,
    loadNextPage,
    setSearchValue: onLocationSearch,
  } = useAsyncSearchableList({
    query: useInfiniteHospitalListQuery,
    defaultSortOrder: { orderBy: 'name', asc: true },
  });

  const onFilterChange = (filtersList: string | string[], filterType: keyof T) => {
    updateFilters(filtersList, filterType);
  };

  return (
    <div className="flex gap-7 items-center">
      <FilterDropdown
        title="Donor Hospital name"
        items={locationList}
        selectedKeys={filters.hospitals as string[]}
        mode="multiple"
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        loadNextPage={loadNextPage}
        onSearchValue={onLocationSearch}
        onSelectedKeysChange={(values) => onFilterChange(values as string[], 'hospitals')}
      />
      <Tabs
        radius="full"
        aria-label="primary contact filter"
        selectedKey={filters.primary as string}
        onSelectionChange={(value) => onFilterChange(value as string, 'primary')}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key="all" title="All" />
        <Tab key="primary" title="Primary contact" />
        <Tab key="secondary" title="Not a primary contact" />
      </Tabs>
      {hasFilters && (
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onClick={clearFilters}>
          Clear all
        </Button>
      )}
    </div>
  );
};
